import { useState } from "react";
import { formatBytes, type Attachment, type JmapClient } from "../lib/jmap";
import { Icon } from "./Icon";

/**
 * Attachments on a received message, using the design's `.atts` / `.att`.
 *
 * The download endpoint needs a bearer token, so this cannot be an `<a href>` —
 * see JmapClient.download(). The bytes are fetched, wrapped in an object URL,
 * handed to a synthetic anchor, and the URL is revoked straight away so the
 * blob does not sit in memory for the life of the tab.
 */
function isInline(a: Attachment): boolean {
  // Any cid-referenced part belongs to the HTML body, not the paperclip list —
  // MessageBody resolves it into the message. Keying off `disposition` too was
  // wrong: plenty of mailers send `disposition: attachment` on a part the HTML
  // then references by Content-ID, which put every signature logo in the
  // attachment list.
  return !!a.cid;
}

export function Attachments({ client, email }: { client: JmapClient; email: { attachments?: Attachment[] | null } }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const files = (email.attachments ?? []).filter((a) => !isInline(a));
  if (files.length === 0) return null;

  async function save(attachment: Attachment) {
    setBusy(attachment.blobId);
    setError(null);
    let url: string | null = null;
    try {
      const blob = await client.download(attachment);
      url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.name ?? "attachment";
      link.click();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download that file.");
    } finally {
      if (url) URL.revokeObjectURL(url);
      setBusy(null);
    }
  }

  return (
    <>
      <div className="atts">
        {files.map((a) => (
          <button
            key={a.blobId}
            className="att"
            onClick={() => void save(a)}
            disabled={busy === a.blobId}
            title={`Download ${a.name ?? "attachment"}`}
          >
            <span className="ic"><Icon name="paperclip" /></span>
            <span>
              <b>{a.name ?? "Untitled"}</b>
              <span>{busy === a.blobId ? "Downloading…" : formatBytes(a.size)}</span>
            </span>
          </button>
        ))}
      </div>
      {error && (
        <div role="alert" style={{ color: "var(--bad)", font: "400 12px/1.4 Inter", padding: "0 18px 12px 68px" }}>
          {error}
        </div>
      )}
    </>
  );
}
